import { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../../utils/axiosConfig';
import { CATEGORIAS, PROYECTO_COLOR_DEFAULT } from './constants';

export const VISTA_GENERAL_SLUG = 'vista-general';

// Un estado virtual por categoria. Misma forma que ProyectoEstado.to_dict() para
// que board, lista y slide-over los consuman sin cambios.
export const ESTADOS_VIRTUALES = CATEGORIAS.map((c, i) => ({
  id: `cat:${c.key}`,
  categoria: c.key,
  nombre: c.label,
  orden: i,
  color: c.color,
}));

/**
 * Datos de "todos los proyectos": las tareas de todos los proyectos del usuario
 * y un proyecto sintetico (estados virtuales por categoria, union de miembros,
 * etiquetas e hitos) con la misma forma que el contexto de un proyecto. Lo usan
 * la vista general (tablero / lista / calendario / gantt) y el Gantt general
 * del modulo, que es el mismo Gantt de un proyecto alimentado con todo.
 */
export const useDatosVistaGeneral = () => {
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTareas = useCallback(async () => {
    const { data } = await axiosInstance.get('/api/tareas');
    setTareas(data || []);
  }, []);

  const fetchProyectos = useCallback(async () => {
    // config=true trae estados/etiquetas/hitos de cada proyecto: los necesita el
    // board para mover tareas y el slide-over para editarlas.
    const { data } = await axiosInstance.get('/api/proyectos', { params: { config: true } });
    setProyectos(data || []);
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchProyectos(), fetchTareas()]);
      } catch (err) {
        console.error('Error cargando la vista general:', err);
        if (vivo) setError('No pudimos cargar la vista general. Intenta de nuevo.');
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [fetchProyectos, fetchTareas]);

  const refetchTareas = useCallback(async () => {
    try {
      await fetchTareas();
    } catch (err) {
      console.error('Error recargando tareas:', err);
    }
  }, [fetchTareas]);

  const refetchProyecto = useCallback(async () => {
    try {
      await fetchProyectos();
    } catch (err) {
      console.error('Error recargando proyectos:', err);
    }
  }, [fetchProyectos]);

  const updateTareaLocal = useCallback((id, patch) => {
    setTareas((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const addTareaLocal = useCallback((nueva) => {
    setTareas((prev) => [nueva, ...prev]);
  }, []);

  // ----- Proyecto sintetico -----

  const proyectosMap = useMemo(() => {
    const m = {};
    proyectos.forEach((p) => { m[p.id] = p; });
    return m;
  }, [proyectos]);

  // Union de miembros / etiquetas / hitos de todos los proyectos: es lo que
  // consumen los filtros del board y los agrupadores de la lista.
  const proyecto = useMemo(() => {
    const miembros = [];
    const vistosMiembro = new Set();
    const etiquetas = [];
    const vistasEtiqueta = new Set();
    const hitos = [];

    proyectos.forEach((p) => {
      (p.miembros || []).forEach((m) => {
        if (vistosMiembro.has(m.empleado_id)) return;
        vistosMiembro.add(m.empleado_id);
        miembros.push(m);
      });
      (p.etiquetas || []).forEach((e) => {
        if (vistasEtiqueta.has(e.id)) return;
        vistasEtiqueta.add(e.id);
        etiquetas.push(e);
      });
      (p.hitos || []).forEach((h) => hitos.push(h));
    });

    miembros.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

    return {
      id: null,
      slug: VISTA_GENERAL_SLUG,
      nombre: 'Vista general',
      color: PROYECTO_COLOR_DEFAULT,
      estados: ESTADOS_VIRTUALES,
      miembros,
      etiquetas,
      hitos,
    };
  }, [proyectos]);

  // Estado real (dentro del proyecto de la tarea) para una categoria dada. El
  // primero por orden: es el sub-estado "de entrada" de esa columna.
  const resolverEstadoId = useCallback((tarea, categoria) => {
    const p = proyectosMap[tarea?.proyecto_id];
    if (!p) return null;
    const candidatos = (p.estados || [])
      .filter((e) => e.categoria === categoria)
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    return candidatos[0] || null;
  }, [proyectosMap]);

  // Mismo contrato que el Outlet context de un proyecto (ProyectoView).
  const contexto = useMemo(() => ({
    proyecto,
    tareas,
    refetchTareas,
    refetchProyecto,
    updateTareaLocal,
    addTareaLocal,
    color: PROYECTO_COLOR_DEFAULT,
    esVistaGeneral: true,
    proyectosMap,
    resolverEstadoId,
  }), [proyecto, tareas, refetchTareas, refetchProyecto, updateTareaLocal, addTareaLocal, proyectosMap, resolverEstadoId]);

  return { loading, error, proyectos, tareas, contexto };
};

export default useDatosVistaGeneral;
